import { Box, Card, Checkbox, CircularProgress, FormControl, FormControlLabel, MenuItem, Select, TextField } from "@mui/material";
import api from "api/api";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { ApiResponse } from "model/apiResponseModel";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const fieldSx = {
    "& .MuiInputBase-root": {
      display: "flex !important",
      placeItems: "unset !important",
      alignItems: "center !important",
      justifyContent: "flex-start !important",
      width: "100% !important",
    },
    "& .MuiInputBase-input": {
      width: "100% !important",
      textAlign: "left !important",
    },
    "& .MuiInputBase-inputMultiline": {
      width: "100% !important",
      textAlign: "left !important",
      display: "block !important",
    },
    "& textarea": {
      width: "100% !important",
      textAlign: "left !important",
    },
  };

  const numberFieldSx = {
    ...fieldSx,
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
    "& input[type=number]::-webkit-outer-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    "& input[type=number]::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
  };

  const selectSx = {
    "& .MuiInputBase-root": {
      display: "flex !important",
      placeItems: "unset !important",
      alignItems: "center !important",
      justifyContent: "flex-start !important",
      width: "100% !important",
    },
    "& .MuiSelect-select": {
      textAlign: "left !important",
      width: "100% !important",
    },
  };

export default function StreakForm() {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [badges, setBadges] = useState([]);
    const [formData, setFormData] = useState({
        day: "",
        reward_type: [], 
        points_reward: "",
        badgeId: "",
    });

    const queryParams = new URLSearchParams(location.search);
    const streakId = queryParams.get("streakId");
    console.log("queryParams: ", queryParams, streakId);
    const isEditMode = Boolean(streakId);


    const getStreakMilestoneDetails = async () => {
        if(!streakId){
            return;
        }
        try {
            setIsLoading(true);

            const response = await api.getStreakMilestoneDetails({streak_id: streakId});
        //   const apiResponse = new ApiResponse(response.status, response.message);
        //   console.log(apiResponse, apiResponse?.data);

            if(response.status === 1){
              const data = response?.data;  
              setFormData({
                day: data?.day,
                points_reward: data?.points_reward,
                badgeId: data?.badgeId,
                reward_type: data?.reward_type === "composite" ? ["point", "badge"] : data?.reward_type === "point" ? ["point"] : ["badge"],
              });
            }
            
        } catch (error) {
            console.error("Error fetching badges:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchBadges = async () => {
        try {
          setIsLoading(true);
    
          const response = await api.getBadges({category: "consistency"});
        //   const apiResponse = new ApiResponse(response.status, response.message);
        //   console.log(apiResponse, apiResponse?.data);

          if(response.status === 1){
            setBadges(response?.data);
          }
          
        } catch (error) {
          console.error("Error fetching badges:", error);
        } finally {
          setIsLoading(false);
        }
      };

     useEffect(() => {
        const loginData = JSON.parse(
            localStorage.getItem("loginData") || sessionStorage.getItem("loginData")
        );

        if (!loginData) {
            navigate("/authentication/sign-in");
        } else {
            fetchBadges();

            if(streakId){
                getStreakMilestoneDetails();
            }
        }
    }, [streakId]);

    const handleChange = (e) => {
        const { name, value, checked } = e.target;

        setFormData((prev) => ({
        ...prev,
        [name]: value,
        }));
    };

    const handleRewardTypeChange = (type) => {
        setFormData((prev) => {
        const exists = prev.reward_type.includes(type);

        let updated = exists
            ? prev.reward_type.filter((t) => t !== type)
            : [...prev.reward_type, type];

            return {
                ...prev,
                reward_type: updated,
                // reset dependent fields
                points_reward: updated.includes("point") ? prev.points_reward : "",
                badgeId: updated.includes("badge") ? prev.badgeId : "",
            };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setIsSubmitting(true);

            const payload = {
                day: Number(formData.day),
                points_reward: formData.points_reward ? Number(formData.points_reward) : null,
                badgeId: formData.badgeId || null,
                reward_type: formData.reward_type
            };

            let response;

            if (isEditMode) {
                response = await api.editStreakMilestone({
                    streak_id: streakId,
                    ...payload,
                });
            } else {
                response = await api.addStreakMilestone(payload);
            }

            if (response.status === 1) {
                navigate("/streaks");
            } else {
                alert(response.message);
            }

        } catch (error) {
            console.error("Error saving streak milestone:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if(isLoading){
        return <div>Loading....</div>
    }

    return (
        <>
        <SoftBox py={3} display="flex" justifyContent="center">
  <SoftBox mb={3} width="100%">
    <Card sx={{ boxShadow: ({ boxShadows: { xxl } }) => xxl }}>
      <SoftBox p={3}>
        {/* Header with improved margin and border bottom */}
        <SoftBox mb={4} pb={2} borderBottom="1px solid #f0f0f0">
          <SoftTypography variant="h5" fontWeight="bold">
            {isEditMode ? "Edit Streak Milestone" : "Add New Streak Milestone"}
          </SoftTypography>
          <SoftTypography variant="button" color="text" fontWeight="regular">
            Define thresholds and rewards for consistent user activity.
          </SoftTypography>
        </SoftBox>

        <form onSubmit={handleSubmit}>
          {/* DAY INPUT - Using a consistent layout */}
          <Box display="flex" alignItems="center" gap={3} mb={3}>
            <SoftTypography variant="body2" fontWeight="medium" sx={{ minWidth: "150px" }}>
              Days
            </SoftTypography>
            <TextField
                fullWidth
                placeholder="e.g. 7"
                type="number"
                name="day"
                value={formData.day}
                onChange={(e) => {
                    const val = e.target.value;

                    // allow only digits (0–9)
                    if (val === "" || /^[0-9]+$/.test(val)) {
                    handleChange({
                        target: {
                        name: "day",
                        value: val,
                        },
                    });
                    }
                }}
                onKeyDown={(e) => {
                    // block everything except digits
                    if (
                    !/[0-9]/.test(e.key) &&
                    ![
                        "Backspace",
                        "Delete",
                        "Tab",
                        "ArrowLeft",
                        "ArrowRight",
                        "ArrowUp",     // ✅ allow
                        "ArrowDown",
                    ].includes(e.key)
                    ) {
                    e.preventDefault();
                    }
                }}
                inputProps={{
                    inputMode: "numeric", // mobile numeric keyboard
                    pattern: "[0-9]*",
                }}
                sx={numberFieldSx}
            />
          </Box>

          {/* REWARD TYPE - Improved Checkbox Styling */}
          <Box display="flex" alignItems="flex-start" gap={3} mb={3}>
            <SoftTypography variant="body2" fontWeight="medium" sx={{ minWidth: "150px", pt: 1 }}>
              Reward Type
            </SoftTypography>
            <Box display="flex" gap={2}>
              <Box
                sx={{
                //   border: "1px solid #ececec",
                //   borderRadius: "8px",
                  pr: 2,
                  backgroundColor: formData.reward_type.includes("point") ? "#f8f9fa" : "transparent",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.reward_type.includes("point")}
                      onChange={() => handleRewardTypeChange("point")}
                    />
                  }
                  label={<SoftTypography variant="button">Points</SoftTypography>}
                  sx={{ ml: 0 }}
                />
              </Box>

              <Box
                sx={{
                //   border: "1px solid #ececec",
                //   borderRadius: "8px",
                  pr: 2,
                  backgroundColor: formData.reward_type.includes("badge") ? "#f8f9fa" : "transparent",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.reward_type.includes("badge")}
                      onChange={() => handleRewardTypeChange("badge")}
                    />
                  }
                  label={<SoftTypography variant="button">Badge</SoftTypography>}
                  sx={{ ml: 0 }}
                />
              </Box>
            </Box>
          </Box>

          {/* CONDITIONAL SECTION WRAPPER */}
          {(formData.reward_type.includes("point") || formData.reward_type.includes("badge")) && (
            <Box
              sx={{
                backgroundColor: "#fcfaff",
                p: 3,
                borderRadius: "12px",
                mb: 3,
                ml: 22,
                border: "1px dashed #d1c4e9",
              }}
            >
              {/* POINTS INPUT */}
              {formData.reward_type.includes("point") && (
                <Box display="flex" alignItems="center" gap={3} mb={formData.reward_type.includes("badge") ? 3 : 0}>
                  <SoftTypography variant="body2" fontWeight="medium" sx={{ minWidth: "150px" }}>
                    Points Reward
                  </SoftTypography>
                  <TextField
                    fullWidth
                    placeholder="Enter amount"
                    type="number"
                    name="points_reward"
                    value={formData.points_reward}
                    onChange={(e) => {
                        const val = e.target.value;

                        // allow only digits (0–9)
                        if (val === "" || /^[0-9]+$/.test(val)) {
                        handleChange({
                            target: {
                                name: "points_reward",
                                value: val,
                            },
                        });
                        }
                    }}
                    onKeyDown={(e) => {
                        // block everything except digits
                        if (
                        !/[0-9]/.test(e.key) &&
                        ![
                            "Backspace",
                            "Delete",
                            "Tab",
                            "ArrowLeft",
                            "ArrowRight",
                            "ArrowUp",     // ✅ allow
                            "ArrowDown",
                        ].includes(e.key)
                        ) {
                            e.preventDefault();
                        }
                    }}
                    inputProps={{
                        inputMode: "numeric", // mobile numeric keyboard
                        pattern: "[0-9]*",
                    }}
                    sx={numberFieldSx}
                  />
                </Box>
              )}

              {/* BADGE SELECT */}
              {formData.reward_type.includes("badge") && (
                <Box display="flex" alignItems="center" gap={3}>
                  <SoftTypography variant="body2" fontWeight="medium" sx={{ minWidth: "150px" }}>
                    Select Badge
                  </SoftTypography>
                  <FormControl fullWidth sx={{ ...selectSx ,backgroundColor: "white" }}>
                    <Select
                      name="badgeId"
                      value={formData.badgeId}
                      onChange={handleChange}
                      displayEmpty
                      sx={{ minHeight: "45px" }}
                    >
                      <MenuItem value="" disabled>
                        Choose a badge...
                      </MenuItem>
                      {badges?.map((b) => (
                        <MenuItem key={b._id} value={b._id}>
                          {b.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Box>
          )}

          {/* SUBMIT BUTTON */}
          <Box display="flex" justifyContent="flex-end" mt={4}>
            <SoftButton
              type="submit"
              variant="gradient"
              color="info"
              size="large"
              sx={{ px: 4, }}
              disabled={isSubmitting}
            >
              {isSubmitting && (
                <CircularProgress size={18} color="inherit" style={{marginRight: "4px"}} />
              )}
              {isSubmitting
                ? isEditMode
                    ? "Updating..."
                    : "Saving..."
                : isEditMode
                ? "Update Milestone"
                : "Save Milestone"}
            </SoftButton>
          </Box>
        </form>
      </SoftBox>
    </Card>
  </SoftBox>
</SoftBox>
       </>      
    )
}